import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './HomePage.css';

const HomePage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <h1 className="hero-title">
            Master Swing Trading with <span className="gradient-text">Advanced AI Technology</span>
          </h1>
          <p className="hero-subtitle">
            Leverage cutting-edge artificial intelligence to identify high-probability swing trading setups. Our AI analyzes thousands of stocks daily to find technical patterns and momentum opportunities in swing trading timeframes.
          </p>
          <div className="hero-buttons">
            <button className="cta-button primary" onClick={() => navigate('/screener/momentum-ai')}>
              Try AI Screener
            </button>
            <button className="cta-button secondary" onClick={() => navigate('/pricing')}>
              View Pricing
            </button>
          </div>
        </div>
        <div className="hero-image">
          <div className="chart-mockup">📈</div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <h2 className="section-title">Powerful Features for Swing Traders</h2>
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">🤖</div>
            <h3>AI-Powered Swing Screeners</h3>
            <p>Our proprietary AI algorithms scan 3,000+ stocks daily to identify high-probability swing trading setups based on technical patterns and momentum signals</p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">📊</div>
            <h3>AI Chart Pattern Detection</h3>
            <p>Machine learning models automatically identify VCP, Flag & Pennant, Narrow Range, and Mountain Scoop patterns at optimal entry points for swing trades</p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">🎯</div>
            <h3>Momentum & Trend AI</h3>
            <p>Advanced AI identifies stocks with strong momentum and trend continuation signals, perfect for swing trading timeframes of 3-21 days</p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">📈</div>
            <h3>AI Backtest Insights</h3>
            <p>Explore historical chart pattern data to understand how similar setups have played out — for educational and informational purposes only</p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">🚀</div>
            <h3>IPO Swing Momentum Scanner</h3>
            <p>AI detects newly listed IPO stocks with explosive swing trading potential, catching bull runs early with data-driven entry signals</p>
          </div>

          <div className="feature-card">
            <div className="feature-icon">📚</div>
            <h3>Swing Trading Mastery Hub</h3>
            <p>Learn proven swing trading strategies, AI-backed stop-loss techniques, and trailing stop methods to maximize profits and minimize risk</p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section">
        <h2 className="section-title">Why Choose Swing Setup AI?</h2>
        <div className="benefits-container">
          <div className="benefit-item">
            <div className="benefit-number">01</div>
            <h3>AI-Powered Efficiency</h3>
            <p>Stop manually scanning thousands of charts. Our AI instantly identifies high-probability chart patterns with 70%+ pattern accuracy, saving you hours daily</p>
          </div>

          <div className="benefit-item">
            <div className="benefit-number">02</div>
            <h3>Data-Driven Swing Trading</h3>
            <p>Scan thousands of stocks using technical pattern recognition — identify potential swing setups based on historical chart patterns</p>
          </div>

          <div className="benefit-item">
            <div className="benefit-number">03</div>
            <h3>Comprehensive Learning Hub</h3>
            <p>Access exclusive educational content with new swing trading concepts, strategies, and techniques added regularly. Learn from market analysis, pattern guides, and expert insights to continuously improve your trading skills</p>
          </div>

          <div className="benefit-item">
            <div className="benefit-number">04</div>
            <h3>Top Screeners Used by Successful Traders</h3>
            <p>Get access to the same powerful AI screeners—Momentum, Rocket Launch AI, VCP, Bullish Flags—used by traders who have achieved consistent success in swing trading across different market conditions</p>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <h2 className="section-title">What Our Users Say</h2>
        <div className="testimonials-grid">
          <div className="testimonial-card">
            <div className="stars">⭐⭐⭐⭐⭐</div>
            <p className="testimonial-text">
              "The AI Momentum Screener identified a strong swing trade setup that I would've completely missed. The AI's pattern recognition is phenomenal—it's like having a professional analyst working 24/7!"
            </p>
            <div className="testimonial-author">
              <div className="author-avatar">RK</div>
              <div>
                <div className="author-name">Rajesh Kumar</div>
                <div className="author-title">Swing Trader</div>
              </div>
            </div>
          </div>

          <div className="testimonial-card">
            <div className="stars">⭐⭐⭐⭐⭐</div>
            <p className="testimonial-text">
              "The AI-powered VCP and Flag screeners identified multiple high-quality swing trade setups last month. The pattern detection accuracy is unmatched in the Indian market."
            </p>
            <div className="testimonial-author">
              <div className="author-avatar">PM</div>
              <div>
                <div className="author-name">Priya Mehta</div>
                <div className="author-title">Full-Time Trader</div>
              </div>
            </div>
          </div>

          <div className="testimonial-card">
            <div className="stars">⭐⭐⭐⭐⭐</div>
            <p className="testimonial-text">
              "As a beginner, the AI screeners simplified swing trading for me. I just follow the AI signals and apply the stop-loss techniques from Learning Hub. The educational content is excellent!"
            </p>
            <div className="testimonial-author">
              <div className="author-avatar">AS</div>
              <div>
                <div className="author-name">Amit Sharma</div>
                <div className="author-title">Part-Time Trader</div>
              </div>
            </div>
          </div>

          <div className="testimonial-card">
            <div className="stars">⭐⭐⭐⭐⭐</div>
            <p className="testimonial-text">
              "The Learning Hub taught me AI-backed stop-loss and trailing stop techniques that transformed my swing trading. I now have a systematic approach to protect capital while letting winners run!"
            </p>
            <div className="testimonial-author">
              <div className="author-avatar">SG</div>
              <div>
                <div className="author-name">Sneha Gupta</div>
                <div className="author-title">Swing Trader</div>
              </div>
            </div>
          </div>

          <div className="testimonial-card">
            <div className="stars">⭐⭐⭐⭐⭐</div>
            <p className="testimonial-text">
              "The Rocket Launch AI identifies potential swing moves early with impressive accuracy. The AI backtests provide valuable insights on pattern behavior—incredibly helpful for decision making!"
            </p>
            <div className="testimonial-author">
              <div className="author-avatar">VR</div>
              <div>
                <div className="author-name">Vikram Reddy</div>
                <div className="author-title">Swing Trader</div>
              </div>
            </div>
          </div>

          <div className="testimonial-card">
            <div className="stars">⭐⭐⭐⭐⭐</div>
            <p className="testimonial-text">
              "I've tested 10+ screeners—none match this AI accuracy for swing trading. The Mountain Scoop AI scanner consistently identifies quality setups with excellent risk-reward potential!"
            </p>
            <div className="testimonial-author">
              <div className="author-avatar">NK</div>
              <div>
                <div className="author-name">Neha Kapoor</div>
                <div className="author-title">Professional Trader</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="stats-grid">
          <div className="stat-item">
            <div className="stat-number">1,700+</div>
            <div className="stat-label">Active Users</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">3,000+</div>
            <div className="stat-label">Stocks Scanned Daily</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">70%+</div>
            <div className="stat-label">Pattern Accuracy</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">24/7</div>
            <div className="stat-label">Support</div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <h2>Ready to Master Swing Trading with AI?</h2>
        <p>Join 1,700+ active users using AI-powered screeners to identify high-probability trading opportunities</p>
        <div className="cta-buttons">
          <button className="cta-button primary" onClick={() => navigate('/screener/momentum-ai')}>
            Try AI Screeners Now
          </button>
          <button className="cta-button secondary" onClick={() => navigate('/learning-hub')}>
            Learn Swing Trading
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="home-footer">
        <div className="home-footer__links">
          <Link to="/privacy-policy" className="home-footer__link">Privacy Policy</Link>
          <span className="home-footer__divider">|</span>
          <Link to="/terms-and-conditions" className="home-footer__link">Terms &amp; Conditions</Link>
          <span className="home-footer__divider">|</span>
          <Link to="/disclaimer" className="home-footer__link">Disclaimer</Link>
          <span className="home-footer__divider">|</span>
          <Link to="/refund-policy" className="home-footer__link">Refund Policy</Link>
          <span className="home-footer__divider">|</span>
          <Link to="/contact" className="home-footer__link">Contact</Link>
          <span className="home-footer__divider">|</span>
          <Link to="/faq" className="home-footer__link">FAQ</Link>
        </div>
        <p className="home-footer__disclaimer">
          ⚠️ SwingSetupAI is a technical analysis tool for educational purposes only. Not investment advice. Not SEBI registered.
          Please consult a SEBI registered adviser before making investment decisions.
        </p>
        <p className="home-footer__copy">© {new Date().getFullYear()} SwingSetupAI. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default HomePage;
